import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { DISTANCE, DEALER_NAME } from './constants';
import { DealerStore } from '@cat-ecom/pcc-components';
import { FiltersWithSort, SortOrder } from './types';
import { StoreAddress } from '../types';
import { normalizeUrl } from 'utils/url';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { CONTACT_US_DEALER } from 'constants/endpoints';

/**
 * Compares 2 store locations based on distance first, then 'city state' second
 * If there are multiple locations with the same lat and long as the closest location, sort those alphabetically by city and state
 */
export const compareStoreLocations = (
  store1: DealerStore,
  store2: DealerStore
) => {
  const dist1 = store1.geo.distance ?? Infinity; // undefined/null values are sorted to bottom of list
  const dist2 = store2.geo.distance ?? Infinity; // undefined/null values are sorted to bottom of list
  const location1 = `${store1.address.city} ${store1.address.state}`;
  const location2 = `${store2.address.city} ${store2.address.state}`;
  const distDiff = dist1 - dist2;
  return distDiff === 0 || Number.isNaN(distDiff)
    ? location1.localeCompare(location2)
    : distDiff;
};

/**
 * Compares 2 store locations based on dealerName first, then 'city state' second
 */
const compareStoreNames = (store1: DealerStore, store2: DealerStore) => {
  const dealerName1 = store1.dealerName;
  const dealerName2 = store2.dealerName;
  const storeName1 = store1.name;
  const storeName2 = store2.name;
  const areNamesEqual = dealerName1 === dealerName2;
  //send undefined to the bottom of the list
  if (!dealerName1) {
    return 1;
  } else if (!dealerName2) {
    return -1;
  }
  return areNamesEqual
    ? storeName1.localeCompare(storeName2)
    : dealerName1.localeCompare(dealerName2);
};

/**
 * Return the list of dealers ordered by distance or dealer name
 */
const getDealersOrderedByProperty = (
  dealers: DealerStore[] = [],
  property: SortOrder = DISTANCE,
  numDisplayed: number = dealers.length
) => {
  if (!dealers.length) {
    return [];
  }
  if (property === DEALER_NAME) {
    return [...dealers].slice(0, numDisplayed).sort(compareStoreNames);
  } else {
    return [...dealers]
      .sort(compareStoreLocations)
      .map((item, index) => ({
        ...item,
        geo: { ...item.geo, isClosest: index === 0 }
      }))
      .slice(0, numDisplayed);
  }
};

const getFilteredDealerStores = (
  dealerStores: DealerStore[],
  filters: FiltersWithSort
) =>
  dealerStores?.filter((store) =>
    store?.services?.some((service) => filters[service] === true)
  );

const areServiceFiltersSelected = (filters?: FiltersWithSort) =>
  !!filters && Object.values(filters).includes(true);

/**
 * Return the list of dealers ordered by distance or dealer name and filtered based on filters object
 */
export const getFilteredAndOrderedDealerStores = (
  dealerStores: DealerStore[] = [],
  numDisplayed?: number,
  filters?: FiltersWithSort
): {
  filteredAndOrderedVisibleDealerStores: DealerStore[];
  allFilteredDealerStoresByDistance: DealerStore[];
} => {
  if (isEmpty(dealerStores)) {
    return {
      allFilteredDealerStoresByDistance: [],
      filteredAndOrderedVisibleDealerStores: []
    };
  }

  // We make a copy of dealerStores in order to avoid mutate the original object
  let allFilteredDealerStoresByDistance = [...dealerStores];
  if (areServiceFiltersSelected(filters)) {
    allFilteredDealerStoresByDistance = getFilteredDealerStores(
      allFilteredDealerStoresByDistance,
      filters
    );
  }

  const filteredAndOrderedVisibleDealerStores = getDealersOrderedByProperty(
    [...allFilteredDealerStoresByDistance],
    filters?.sortOrder,
    numDisplayed
  );

  return {
    filteredAndOrderedVisibleDealerStores,
    allFilteredDealerStoresByDistance
  };
};

/**
 * Return the list of dealers after checking if there are multiple dealers with the same lat/long
 * as the closest dealer, and sorting them by dealer store city and state
 */
export const sortClosestVisibleMapStores = (stores: DealerStore[]) => {
  const filteredByClosestCoordinates = stores.filter(
    (store) =>
      store.geo.latitude === stores[0].geo.latitude &&
      store.geo.longitude === stores[0].geo.longitude
  );
  if (filteredByClosestCoordinates.length > 1) {
    filteredByClosestCoordinates.sort((store1, store2) =>
      compareStoreLocations(store1, store2)
    );
    const sortedStores = [...stores];
    sortedStores.splice(
      0,
      filteredByClosestCoordinates.length,
      ...filteredByClosestCoordinates
    );
    return sortedStores;
  }
  return stores;
};

export const getLatLng = (lat: string, lng: string) => ({
  lat: parseFloat(lat),
  lng: parseFloat(lng)
});

export const roundCompare = (x: number, y: number, precision: number) =>
  x.toPrecision(precision) === y.toPrecision(precision);

export const getLocationAddress = (
  storeAddress?: StoreAddress
): string | undefined => {
  if (storeAddress) {
    const { line1, city, state, zipCode, country } = storeAddress;
    return (
      [line1, city, state, zipCode, country].filter((v) => v).join(', ') ||
      undefined
    );
  }
  return undefined;
};
export const generateContactUrl = (catalogId, langId) => {
  const returnContactUrl = (dealerStoreId?: string) =>
    normalizeUrl(
      replaceTokensInString(
        `${CONTACT_US_DEALER}`,
        catalogId,
        langId,
        dealerStoreId
      )
    );
  return returnContactUrl;
};
